import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import BottomFixedButton from "components/bottomFixedButton/BottomFixedButton";
import Header from "components/header/Header";
import MedicalAssistant from "components/MedicalAssistant/MedicalAssistant";
import { useCurrentUser } from "hooks/useCurrentUser";
import Spacer from "components/Spacer/Spacer";
import Images from "assets/images";
import { useHistory } from "react-router-dom";
import { useApiMutation } from "hooks/useApiMutation";
import { getVisitTerms } from "api/actions/VisitActions";
import CSSLoader from "components/cssLoader/CSSLoader";
import useToastify from "hooks/useToastify";
import useUpdateDermatologyVisit from "hooks/visit/useUpdateDermatologyVisit";
import { useDermatologyVisitFlow } from "hooks/useVisitFlow";
import { trackDropOffRate, trackPageView } from "helpers/analytics";

const codeOfConductBackup = [
  "I understand SkyMD's telehealth services are provided by a medical group of providers, similar to standard in-person clinical visits.",
  "Each time I submit a medical visit, I agree that I am initiating a request for a consultation with at least one medical professional, asking for their unbiased medical opinion, which will include a diagnosis and recommended treatment plan.",
  "I understand that I will have a role in any recommended treatment plan and that by not following the advice and plan of my provider, I may adversely affect the outcome of my treatment.",
  "I understand that my assigned medical professional knows more than what internet searches tell me or what I can deduce through my own self-service efforts, which is why I chose to seek their professional opinion and submit my visit.",
  "I understand that I will communicate with my telehealth provider(s) and staff no differently than if I were standing in front of them, meaning I agree to be banned from the medical platform if I am rude, yell, curse, or otherwise act in an inappropriate social manner.",
  "I understand that these main attestation points are also contained in SkyMD's complete terms and conditions, which are located <a href='https://www.skymd.com/tnc'>here</a>."
];

function DermatologyVisitTerms() {
  const history = useHistory();
  const [selected, setSelected] = useState([]);
  const [terms, setTerms] = useState(null);
  const { userDetails } = useCurrentUser();
  const { mutate: fetchTerms, loading: fetchingTerms } = useApiMutation(
    getVisitTerms
  );
  const { showAlert } = useToastify();
  const { updateVisit, updatingVisit } = useUpdateDermatologyVisit();
  const { incompleteData } = useDermatologyVisitFlow();

  const displayTerms = terms && terms.length > 0 ? terms : codeOfConductBackup;

  useEffect(() => {
    const init = async () => {
      const result = await fetchTerms("dermatology");

      setTerms(result?.payload);
    };

    init();
  }, [fetchTerms]);

  useEffect(() => {
    setSelected(
      incompleteData?.accepted_tnc
        ? Array.from({ length: displayTerms.length }, (_, i) => i)
        : []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terms, incompleteData]);

  useEffect(() => {
    if (userDetails?.id) {
      trackPageView("dermatology-coc", userDetails.id);
      trackDropOffRate("dermatology-coc", userDetails.id);
    }
  }, [userDetails]);

  function onSelect(index) {
    if (selected?.includes(index)) {
      setSelected((prev) => prev?.filter((el) => el !== index));
    } else {
      setSelected((prev) => [...prev, index]);
    }
  }

  const acceptTerms = () => {
    if (incompleteData?.accepted_tnc) {
      history.push("/visit/dermatology/checkout");
    } else {
      updateVisit({
        payload: {
          case: {
            accepted_tnc: true,
          },
        },
        change_current_screen: false,
        screen_to_complete: null,
        callBack: () => {
          showAlert("Terms Accepted", "success");
          history.push("/visit/dermatology/checkout");
        },
      });
    }
  };

  return (
    <div className="h-screen bg-veryLightBlue">
      <Header title="SkyMD's Code of Conduct" hideBackBtn={fetchingTerms} />
      <div className="overflow-y-auto bg-white h-eliminateHeaderWithFooter wrapper">
        <Spacer height={30} />
        <MedicalAssistant
          xOffset={23}
          content={
            <p>
              {userDetails?.first_name}, please accept SkyMD’s code of conduct
              before proceeding.
            </p>
          }
        />
        <Spacer height={27} />
        <div className="pb-16 bg-alternativeWhite">
          <div className="px-12">
            {fetchingTerms || terms === null ? (
              <div className="flex items-center justify-center flex-1 py-10">
                <CSSLoader className="w-16 h-16" color="orange" />
              </div>
            ) : (
              displayTerms.map((term, index) => {
                return (
                  <div className="py-10" key={index}>
                    <div
                      onClick={() => onSelect(index)}
                      className="flex cursor-pointer"
                    >
                      <img
                        className="w-12 h-12 flex-shrink-0"
                        src={
                          selected?.includes(index)
                            ? Images.checkedOrange
                            : Images.uncheckedCircle
                        }
                        alt="checked"
                      />
                      <p
                        className="pt-1 pl-5 text-2xl font-light leading-8"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(term),
                        }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      <BottomFixedButton
        disabled={fetchingTerms || updatingVisit || selected?.length !== displayTerms.length}
        loading={updatingVisit}
        onClick={acceptTerms}
        label="Accept & Continue"
      />
    </div>
  );
}

export default DermatologyVisitTerms;
